// Motion engine for living SVG illustrations: a vanilla port of rossark-motion-skill's
// templates/react-native/motion.ts (see MOTION.md). Everything that moves is a pure
// function of one clock's time `t` in ms; the clocks run on requestAnimationFrame.

/** CSS-style cubic-bezier(x1, y1, x2, y2) as a function of progress 0 → 1. */
function bezier(x1, y1, x2, y2) {
	const at = (a, b, s) => 3 * a * s * (1 - s) ** 2 + 3 * b * s * s * (1 - s) + s ** 3;
	return (p) => {
		if (p <= 0 || p >= 1) return p;
		let lo = 0;
		let hi = 1;
		let s = p;
		for (let i = 0; i < 24; i++) {
			if (at(x1, x2, s) < p) lo = s;
			else hi = s;
			s = (lo + hi) / 2;
		}
		return at(y1, y2, s);
	};
}

/** Things that travel (gaze, sway, a head turning, a lid closing). */
export const EASE_IN_OUT = bezier(0.77, 0, 0.175, 1);
/** Things that open or arrive (a lid reopening, a prop appearing). */
export const EASE_OUT = bezier(0.23, 1, 0.32, 1);
/** Only for things leaving or falling away. */
export const EASE_IN = bezier(0.55, 0, 1, 0.45);

/**
 * ApexCharts `chart.animations` for every animated chart: series draw on with ease-out and a short
 * stagger, final frame = the data. ApexCharts already skips them under prefers-reduced-motion.
 */
export const chartAnimations = () => ({
	speed: 500,
	easing: EASE_OUT,
	animateGradually: { enabled: true, delay: 60 },
});

/**
 * Keyframes `[ms, value, "out"?]`: each segment eases in-out, or out when marked "out".
 * Stops at every key: right for ONE move (a blink). The last key should equal the first.
 */
export function track(t, keys) {
	if (t <= keys[0][0]) return keys[0][1];
	for (let i = 1; i < keys.length; i++) {
		const [time, value, curve] = keys[i];
		if (t <= time) {
			const [time0, value0] = keys[i - 1];
			const p = time > time0 ? (t - time0) / (time - time0) : 1;
			return value0 + (value - value0) * (curve === "out" ? EASE_OUT(p) : EASE_IN_OUT(p));
		}
	}
	return keys[keys.length - 1][1];
}

/**
 * A smooth path through key poses `[ms, value]` (monotone cubic): continuous speed,
 * never overshoots a key. For acting; repeat a value to hold.
 */
export function flow(t, k) {
	const n = k.length;
	if (t <= k[0][0]) return k[0][1];
	if (t >= k[n - 1][0]) return k[n - 1][1];
	let i = 0;
	while (k[i + 1][0] < t) i++;
	const slope = (j) => (k[j + 1][1] - k[j][1]) / (k[j + 1][0] - k[j][0]);
	const tangent = (j) => {
		if (j === 0 || j === n - 1) return 0;
		const a = slope(j - 1);
		const b = slope(j);
		if (a * b <= 0) return 0;
		const w0 = 2 * (k[j + 1][0] - k[j][0]) + (k[j][0] - k[j - 1][0]);
		const w1 = k[j + 1][0] - k[j][0] + 2 * (k[j][0] - k[j - 1][0]);
		return (w0 + w1) / (w0 / a + w1 / b);
	};
	const h = k[i + 1][0] - k[i][0];
	const s = (t - k[i][0]) / h;
	const s2 = s * s;
	const s3 = s2 * s;
	return (
		(2 * s3 - 3 * s2 + 1) * k[i][1] +
		(s3 - 2 * s2 + s) * h * tangent(i) +
		(-2 * s3 + 3 * s2) * k[i + 1][1] +
		(s3 - s2) * h * tangent(i + 1)
	);
}

/** 0 → 1 inside the window [a, b] (eased), 0 before, 1 after: one beat of a scene. */
export function seg(t, a, b, curve = "out") {
	if (t <= a) return 0;
	if (t >= b) return 1;
	const p = (t - a) / (b - a);
	return curve === "out" ? EASE_OUT(p) : EASE_IN_OUT(p);
}

/** Smooth -1 → 1 back and forth; `phase` (0 → 1) offsets one item from the next. */
export const wave = (t, period, phase = 0) => Math.sin((t / period + phase) * Math.PI * 2);

/** Progress through a loop, 0 → 1 (drift, falling items). */
export function loop(t, period, phase = 0) {
	const p = (t / period + phase) % 1;
	return p < 0 ? p + 1 : p;
}

/** 0 at both ends, 1 in the middle: fade in and out. */
export const bell = (p) => Math.sin(Math.PI * Math.min(1, Math.max(0, p)));

/** Stable pseudo-random [0, 1) for item `i`. Never Math.random() per frame. */
export function seeded(i, salt = 0) {
	const x = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
	return x - Math.floor(x);
}

const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");

// Runs only while `el` is on screen, the tab is visible and motion is allowed; time
// resumes where it paused. Reduced motion draws the still frame: 0 for loops, the end
// for one-shots.
function runClock(el, draw, { period, duration }) {
	const end = duration ?? Number.POSITIVE_INFINITY;
	let elapsed = 0;
	let last = null;
	let frame = 0;
	let onScreen = false;
	const live = () => onScreen && !document.hidden && !reducedMotion.matches && elapsed < end;
	const render = () => {
		if (reducedMotion.matches) draw(duration ?? 0);
		else draw(period ? elapsed % period : Math.min(elapsed, end));
	};
	const tick = (now) => {
		elapsed += last === null ? 0 : now - last;
		last = now;
		render();
		frame = live() ? requestAnimationFrame(tick) : 0;
	};
	const update = () => {
		cancelAnimationFrame(frame);
		last = null;
		frame = live() ? requestAnimationFrame(tick) : 0;
		if (!frame) render();
	};
	new IntersectionObserver(([entry]) => {
		onScreen = entry.isIntersecting;
		update();
	}).observe(el);
	document.addEventListener("visibilitychange", update);
	reducedMotion.addEventListener("change", update);
	render();
}

/** Idle life: `draw(t)` with t looping 0 → period ms. Frame 0 must be a good drawing. */
export const loopClock = (el, period, draw) => runClock(el, draw, { period });

/** A scene that plays once when `el` first shows, then holds its final frame. */
export const oneShotClock = (el, duration, draw) => runClock(el, draw, { duration });
